/**
 * <eks-ymm-finder> — Year/Make/Model vehicle finder for Executive Key Supply.
 *
 * Port of the executivecarkeys.com homepage finder (eck-homepage.js initYmm):
 * cascading Year → Make → Model dropdowns built from live product fitment
 * tags (year:/make:/model:), then navigates to Shopify's native tag-filtered
 * collection URL, e.g. /collections/all/year:2020+make:toyota+model:camry.
 *
 * Data source difference from ECK: ECK reads tags via the Storefront API
 * with an embedded token; this store has no storefront token, so we read
 * the public /products.json endpoint instead (same tags, no auth). Pages
 * of 250, capped at MAX_PRODUCTS like the ECK original.
 *
 * MODEL OWNERSHIP (2026-07-05): multi-make products (universal remotes)
 * carry make:+model: tags for EVERY make they fit, so the naive tag
 * intersection contaminated the Model dropdown (Nissan models under
 * Chevrolet, etc.). Fix: a model is only listed under a make that OWNS it.
 * Ownership = makes seen on SINGLE-make products (derived at runtime, so
 * new catalog products self-attribute) plus the curated MODEL_MAKE_OVERRIDES
 * below for models that only exist on multi-make products. Models unknown
 * to both maps fall back to the old behavior (shown, never hidden).
 * Results pages are unaffected — they already filter by all three tags.
 */
const MAX_PRODUCTS = 1200;
const PAGE_SIZE = 250;
let productDataPromise = null;

const STRINGS = {
  loading: "Loading vehicle fitment...",
  unavailable: "Vehicle fitment is unavailable right now.",
  noMakes: "No makes were found for that year.",
  noModels: "No models were found for that year and make.",
  chooseFirst: "Choose a year, make, and model first.",
};

/* Curated make attribution for models that only appear on multi-make
   products (tags alone can't attribute them). Keys/values match tag
   values (lowercase, handleized). */
const MODEL_MAKE_OVERRIDES = {
  /* Ram trucks & vans (2026-09-26): tagged under BOTH make:Dodge and make:RAM
     so customers find them either way; these models belong to those two only. */
  "ram-1500": ["dodge", "ram"], "ram-2500": ["dodge", "ram"], "ram-3500": ["dodge", "ram"],
  "ram-4500": ["dodge", "ram"], "ram-5500": ["dodge", "ram"],
  "promaster": ["dodge", "ram"], "promaster-city": ["dodge", "ram"],
  "200": ["chrysler"], "240sx": ["nissan"], "300": ["chrysler"], "300m": ["chrysler"],
  "350z": ["nissan"], "500": ["fiat"], "500l": ["fiat"], "500x": ["fiat"],
  "86": ["toyota"], "acclaim": ["plymouth"], "achieva": ["oldsmobile"],
  "aerostar": ["ford"], "alero": ["oldsmobile"], "allure": ["buick"],
  "ascender": ["isuzu"], "aspen": ["chrysler", "dodge"], "astra": ["saturn"],
  "astro": ["chevrolet"], "aura": ["saturn"], "aurora": ["oldsmobile"],
  "avalanche": ["chevrolet"], "avenger": ["dodge"], "aztek": ["pontiac"],
  "b-series": ["mazda"], "blackwood": ["lincoln"], "bonneville": ["pontiac"],
  "borrego": ["kia"], "bravada": ["oldsmobile"], "breeze": ["plymouth"],
  "c30": ["volvo"], "caliber": ["dodge"], "california": ["ferrari"],
  "canyon": ["gmc"], "caprice": ["chevrolet"], "captiva-sport": ["chevrolet"],
  "caravan": ["dodge"], "cascada": ["buick"], "cavalier": ["chevrolet"],
  "celica": ["toyota"], "century": ["buick"], "challenger": ["dodge"],
  "charger": ["dodge"], "cirrus": ["chrysler"], "cityexpress": ["chevrolet"],
  "cl": ["acura"], "cobalt": ["chevrolet"], "colorado": ["chevrolet"],
  "commander": ["jeep"], "concorde": ["chrysler"], "contour": ["ford"],
  "corvette": ["chevrolet"], "cougar": ["mercury"], "crossfire": ["chrysler"],
  "crown-victoria": ["ford"], "csx": ["acura"], "dakota": ["dodge"],
  "denali": ["gmc"], "deville": ["cadillac"], "discovery-sport": ["landrover"],
  "durango": ["dodge"], "econoline": ["ford"], "el": ["acura"],
  "entourage": ["hyundai"], "envoy": ["gmc"], "equator": ["suzuki"],
  "escort": ["ford"], "ex35": ["infiniti"], "ex37": ["infiniti"],
  "excursion": ["ford"], "express": ["chevrolet"], "f-pace": ["jaguar"],
  "f-type": ["jaguar"], "five-hundred": ["ford"], "fj-cruiser": ["toyota"],
  "fr-s": ["scion"], "freestar": ["ford"], "freestyle": ["ford"],
  "fx35": ["infiniti"], "fx45": ["infiniti"], "fx50": ["infiniti"],
  "g20": ["infiniti"], "g25": ["infiniti"], "g35": ["infiniti"],
  "g37": ["infiniti"], "g5": ["pontiac"], "g6": ["pontiac"],
  "grand-am": ["pontiac"], "grand-caravan": ["dodge"],
  "grand-marquis": ["mercury"], "grand-prix": ["pontiac"],
  "grand-voyager": ["plymouth", "chrysler"], "gransport": ["maserati"],
  "granturismo": ["maserati"], "gt": ["ford"], "h2": ["hummer"],
  "h3": ["hummer"], "hhr": ["chevrolet"], "hombre": ["isuzu"],
  "i30": ["infiniti"], "i35": ["infiniti"], "im": ["scion", "toyota"],
  "integra": ["acura"], "intrepid": ["dodge"], "intrigue": ["oldsmobile"],
  "jimmy": ["gmc"], "journey": ["dodge"], "kodiak": ["chevrolet"],
  "lacrosse": ["buick"], "laredo": ["jeep"], "lebaron": ["chrysler"],
  "lesabre": ["buick"], "lhs": ["chrysler"], "liberty": ["jeep"],
  "lr2": ["landrover"], "lr4": ["landrover"], "ls": ["lincoln"],
  "ltd": ["ford"], "lucerne": ["buick"], "m37": ["infiniti"],
  "m56": ["infiniti"], "magnum": ["dodge"], "marauder": ["mercury"],
  "mariner": ["mercury"], "mark-lt": ["lincoln"], "micra": ["nissan"],
  "milan": ["mercury"], "mks": ["lincoln"], "mkt": ["lincoln"],
  "montana": ["pontiac"], "monte-carlo": ["chevrolet"], "montego": ["mercury"],
  "monterey": ["mercury"], "mountaineer": ["mercury"], "mystique": ["mercury"],
  "neon": ["dodge", "plymouth"], "new-yorker": ["chrysler"], "nitro": ["dodge"],
  "nsx": ["acura"], "nv": ["nissan"], "outlook": ["saturn"],
  "park-avenue": ["buick"], "patriot": ["jeep"], "prelude": ["honda"],
  "prowler": ["plymouth", "chrysler"], "pt-cruiser": ["chrysler"],
  "pursuit": ["pontiac"], "q40": ["infiniti"], "q45": ["infiniti"],
  "q70": ["infiniti"], "quattroporte": ["maserati"], "qx4": ["infiniti"],
  "qx50": ["infiniti"], "qx56": ["infiniti"], "raider": ["dodge", "mitsubishi"],
  "rainier": ["buick"], "relay": ["saturn"], "rendezvous": ["buick"],
  "riviera": ["buick"], "rondo": ["kia"], "routan": ["volkswagen"],
  "rsx": ["acura"], "rubicon": ["jeep"], "s10": ["chevrolet"],
  "s40": ["volvo"], "s60": ["volvo"], "s80": ["volvo"], "sable": ["mercury"],
  "safari": ["gmc"], "savana": ["gmc"], "sebring": ["chrysler"],
  "seville": ["cadillac"], "sierra": ["gmc"], "silhouette": ["oldsmobile"],
  "sky": ["saturn"], "skylark": ["buick"], "solstice": ["pontiac"],
  "sonoma": ["gmc"], "spirit": ["dodge"], "stratus": ["dodge"],
  "sunfire": ["pontiac"], "supra": ["toyota"], "terraza": ["buick"],
  "thunderbird": ["ford"], "tiburon": ["hyundai"], "torrent": ["pontiac"],
  "town-car": ["lincoln"], "townandcountry": ["chrysler"], "tracer": ["mercury"],
  "trans-sport": ["pontiac"], "tribute": ["mazda"], "typhoon": ["gmc"],
  "uplander": ["chevrolet"], "v50": ["volvo"], "v60": ["volvo"],
  "v70": ["volvo"], "venture": ["chevrolet"], "verano": ["buick"],
  "vibe": ["pontiac"], "villager": ["mercury"], "viper": ["dodge"],
  "vision": ["eagle"], "windstar": ["ford"], "xb": ["scion"],
  "xc60": ["volvo"], "xc70": ["volvo"], "xe": ["jaguar"], "xj": ["jaguar"],
  "xk8": ["jaguar"], "xl-7": ["suzuki"], "xlr": ["cadillac"],
  "xterra": ["nissan"], "yukon": ["gmc"], "zephyr": ["lincoln", "mercury"],
};

/* Display names for makes that don't title-case cleanly. */
const MAKE_LABELS = { bmw: "BMW", gmc: "GMC", landrover: "Land Rover", ram: "RAM" };

/* Display names for models whose tag value doesn't title-case cleanly. */
const MODEL_LABELS = {
  "ram-1500": "Ram 1500", "ram-2500": "Ram 2500", "ram-3500": "Ram 3500",
  "ram-4500": "Ram 4500", "ram-5500": "Ram 5500",
  "promaster": "ProMaster (Full-Size)", "promaster-city": "ProMaster City",
  /* 2026-10-02: models that came out as "Gv70", "Es350", "Cr-V", "Santafe" etc. */
  "240sx": "240SX", "300m": "300M", "350z": "350Z", "370z": "370Z", "4-runner": "4Runner",
  "500l": "500L", "500x": "500X", "c-hr": "C-HR", "captiva-sport": "Captiva Sport",
  "cc": "CC", "cityexpress": "City Express", "cl": "CL", "cr-v": "CR-V", "cr-z": "CR-Z",
  "crown-victoria": "Crown Victoria", "ct200h": "CT200h", "cx-3": "CX-3", "cx-30": "CX-30",
  "cx-5": "CX-5", "cx-50": "CX-50", "cx-7": "CX-7", "cx-70": "CX-70", "cx-9": "CX-9",
  "cx-90": "CX-90", "deville": "DeVille", "discovery-sport": "Discovery Sport",
  "e-golf": "e-Golf", "eclipse-cross": "Eclipse Cross", "ecosport": "EcoSport", "el": "EL",
  "elantra-gt": "Elantra GT", "electrified-g80": "Electrified G80",
  "electrified-gv70": "Electrified GV70", "encoregx": "Encore GX", "eos": "Eos",
  "es300": "ES300", "es300h": "ES300h", "es330": "ES330", "es350": "ES350", "ex35": "EX35",
  "ex37": "EX37", "fit": "Fit", "five-hundred": "Five Hundred", "fj-cruiser": "FJ Cruiser",
  "fr-s": "FR-S", "fx35": "FX35", "fx45": "FX45", "fx50": "FX50", "gl": "GL",
  "golf-alltrack": "Golf Alltrack", "golf-r": "Golf R",
  "golf-sportwagen": "Golf SportWagen", "golf-wagon": "Golf Wagon", "grand-am": "Grand Am",
  "grand-caravan": "Grand Caravan", "grand-cherokee": "Grand Cherokee",
  "grand-marquis": "Grand Marquis", "grand-prix": "Grand Prix",
  "grand-voyager": "Grand Voyager", "grand-wagoneer": "Grand Wagoneer",
  "gransport": "GranSport", "granturismo": "GranTurismo", "gs-f": "GS-F", "gs300": "GS300",
  "gs350": "GS350", "gs400": "GS400", "gs430": "GS430", "gs450h": "GS450h",
  "gs460": "GS460", "gt": "GT", "gv70": "GV70", "gv80": "GV80", "gx460": "GX460",
  "gx470": "GX470", "hr-v": "HR-V", "hs250": "HS250", "i-miev": "i-MiEV", "i3": "i3",
  "i8": "i8", "im": "iM", "iq": "iQ", "is-c": "IS-C", "is-f": "IS-F", "is200t": "IS200t",
  "is250": "IS250", "is300": "IS300", "is350": "IS350", "jx35": "JX35",
  "kona-electric": "Kona Electric", "lacrosse": "LaCrosse", "landcruiser": "Land Cruiser",
  "lebaron": "LeBaron", "lesabre": "LeSabre", "ls": "LS", "ls400": "LS400",
  "ls430": "LS430", "ls460": "LS460", "ls500": "LS500", "ls500h": "LS500h",
  "ls600h": "LS600h", "lx470": "LX470", "lx570": "LX570", "mark-lt": "Mark LT",
  "ml-class": "ML-Class", "monte-carlo": "Monte Carlo", "montero-sport": "Montero Sport",
  "mx-5": "MX-5", "mx6": "MX-6", "new-yorker": "New Yorker", "nv": "NV",
  "nx200t": "NX200t", "nx300": "NX300", "nx300h": "NX300h",
  "outlander-sport": "Outlander Sport", "park-avenue": "Park Avenue",
  "pt-cruiser": "PT Cruiser", "qx50": "QX50", "qx56": "QX56", "qx60": "QX60",
  "rangerover": "Range Rover", "rangerover-evoque": "Range Rover Evoque",
  "rangerover-sport": "Range Rover Sport", "rangerover-velar": "Range Rover Velar",
  "rav4": "RAV4", "rc-f": "RC-F", "rc200t": "RC200t", "rc300": "RC300", "rc350": "RC350",
  "rio": "Rio", "rl": "RL", "rx-8": "RX-8", "rx300": "RX300", "rx330": "RX330",
  "rx350": "RX350", "rx400h": "RX400h", "rx450h": "RX450h", "rx450hl": "RX450hL",
  "santa-cruz": "Santa Cruz", "santafe": "Santa Fe", "sc300": "SC300", "sc400": "SC400",
  "sc430": "SC430", "sky": "Sky", "sl": "SL", "sportwagen": "SportWagen", "ss": "SS",
  "tc": "tC", "tl": "TL", "town-car": "Town Car", "townandcountry": "Town & Country",
  "trans-sport": "Trans Sport", "transit-connect": "Transit Connect", "tt": "TT",
  "ux200": "UX200", "ux250h": "UX250h", "versa-note": "Versa Note", "vue": "Vue",
  "xb": "xB", "xc40": "XC40", "xc60": "XC60", "xc70": "XC70", "xc90": "XC90", "xd": "xD",
  "xe": "XE", "xf": "XF", "xj": "XJ", "xk": "XK", "xl-7": "XL-7",
};

const normalizeTag = (tag) => String(tag || "").trim().toLowerCase();
const tagValue = (tag) => normalizeTag(tag).split(":").slice(1).join(":") || tag;
const formatTagLabel = (tag, type) => {
  const value = tagValue(tag);
  if (type === "year") return value;
  if (type === "model" && MODEL_LABELS[value]) return MODEL_LABELS[value];
  if (type === "make" && MAKE_LABELS[value]) return MAKE_LABELS[value];
  if (type === "model" && value.length === 3) return value.toUpperCase();
  return value.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};
const sortTags = (tags) =>
  tags.sort((a, b) =>
    tagValue(a).localeCompare(tagValue(b), undefined, { numeric: true, sensitivity: "base" }),
  );
const tagsByPrefix = (products, prefix) =>
  sortTags(Array.from(new Set(products.flatMap((tags) => tags.filter((t) => t.startsWith(prefix))))));
const productsWithTags = (products, required) =>
  products.filter((tags) => required.every((t) => tags.includes(t)));
const buildTagUrl = (allProductsUrl, tags) => {
  const baseUrl = String(allProductsUrl || "/collections/all").replace(/\/$/, "");
  return `${baseUrl}/${tags.join("+")}`;
};

function fetchProductData(productsUrl) {
  if (!productDataPromise) {
    productDataPromise = (async () => {
      const products = [];
      let page = 1;
      while (products.length < MAX_PRODUCTS) {
        const response = await fetch(`${productsUrl}?limit=${PAGE_SIZE}&page=${page}`);
        if (!response.ok) throw new Error("YMM product tags could not be loaded.");
        const data = await response.json().catch(() => null);
        if (!data || !Array.isArray(data.products)) {
          throw new Error("YMM product tags could not be loaded.");
        }
        data.products.forEach((p) => {
          products.push((p.tags || []).map(normalizeTag).filter(Boolean));
        });
        if (data.products.length < PAGE_SIZE) break;
        page += 1;
      }
      // Runtime ownership: model -> Set of makes, from single-make products.
      const owner = new Map();
      products.forEach((tags) => {
        const makes = tags.filter((t) => t.startsWith("make:")).map(tagValue);
        if (makes.length !== 1) return;
        tags.forEach((t) => {
          if (!t.startsWith("model:")) return;
          const model = tagValue(t);
          if (!owner.has(model)) owner.set(model, new Set());
          owner.get(model).add(makes[0]);
        });
      });
      return { products, owner };
    })();
    // let a transient failure retry on the next interaction
    productDataPromise.catch(() => {
      productDataPromise = null;
    });
  }
  return productDataPromise;
}

/* A model is shown under a make when the make is among its claimants
   (runtime owners ∪ curated overrides). Unclaimed models fall back to
   visible so a new product can never vanish from the finder. */
function modelBelongsToMake(modelTag, make, owner) {
  const model = tagValue(modelTag);
  const claims = new Set(MODEL_MAKE_OVERRIDES[model] || []);
  const dynamic = owner.get(model);
  if (dynamic) dynamic.forEach((m) => claims.add(m));
  if (claims.size === 0) return true;
  return claims.has(make);
}

class EksYmmFinder extends HTMLElement {
  connectedCallback() {
    this.yearSel = this.querySelector("[data-ymm-year]");
    this.makeSel = this.querySelector("[data-ymm-make]");
    this.modelSel = this.querySelector("[data-ymm-model]");
    this.button = this.querySelector("[data-ymm-button]");
    this.status = this.querySelector("[data-ymm-status]");

    if (!this.yearSel || !this.makeSel || !this.modelSel || !this.button) {
      return;
    }

    this.productsUrl = this.dataset.productsUrl || "/products.json";
    this.allProductsUrl = this.dataset.allProductsUrl || "/collections/all";
    this.placeholders = {
      year: this.getPlaceholder(this.yearSel),
      make: this.getPlaceholder(this.makeSel),
      model: this.getPlaceholder(this.modelSel),
    };

    this.resetSelect(this.yearSel, this.placeholders.year);
    this.resetSelect(this.makeSel, this.placeholders.make);
    this.resetSelect(this.modelSel, this.placeholders.model);
    this.setSubmitDisabled(true);
    this.setStatus(STRINGS.loading);

    fetchProductData(this.productsUrl)
      .then(({ products }) => {
        const years = tagsByPrefix(products, "year:");
        if (!years.length) {
          this.setStatus(STRINGS.unavailable, true);
          return;
        }
        this.renderSelect(this.yearSel, years.reverse(), this.placeholders.year, "year");
        this.setStatus("");
      })
      .catch(() => {
        this.setStatus(STRINGS.unavailable, true);
      });

    this.yearSel.addEventListener("change", () => this.onYearChange());
    this.makeSel.addEventListener("change", () => this.onMakeChange());
    this.modelSel.addEventListener("change", () => this.onModelChange());
    this.button.addEventListener("click", () => this.onSearch());
  }

  getPlaceholder(select) {
    return (select.options[0] && select.options[0].textContent) || "";
  }

  resetSelect(select, placeholder) {
    select.replaceChildren();
    const option = document.createElement("option");
    option.value = "";
    option.textContent = placeholder;
    select.append(option);
    select.disabled = true;
  }

  renderSelect(select, tags, placeholder, type) {
    this.resetSelect(select, placeholder);
    tags.forEach((tag) => {
      const option = document.createElement("option");
      option.value = tag;
      option.textContent = formatTagLabel(tag, type);
      select.append(option);
    });
    select.disabled = tags.length === 0;
  }

  setStatus(message, isError = false) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
    this.status.dataset.ymmStatus = isError ? "error" : "ready";
  }

  setSubmitDisabled(disabled) {
    this.button.disabled = disabled;
    this.button.setAttribute("aria-disabled", String(disabled));
  }

  selection() {
    return {
      year: normalizeTag(this.yearSel.value),
      make: normalizeTag(this.makeSel.value),
      model: normalizeTag(this.modelSel.value),
    };
  }

  onYearChange() {
    this.resetSelect(this.makeSel, this.placeholders.make);
    this.resetSelect(this.modelSel, this.placeholders.model);
    this.setSubmitDisabled(true);
    this.setStatus("");
    const { year } = this.selection();
    if (!year) return;
    fetchProductData(this.productsUrl)
      .then(({ products }) => {
        const makes = tagsByPrefix(productsWithTags(products, [year]), "make:");
        this.renderSelect(this.makeSel, makes, this.placeholders.make, "make");
        if (!makes.length) this.setStatus(STRINGS.noMakes, true);
      })
      .catch(() => this.setStatus(STRINGS.unavailable, true));
  }

  onMakeChange() {
    this.resetSelect(this.modelSel, this.placeholders.model);
    this.setSubmitDisabled(true);
    this.setStatus("");
    const { year, make } = this.selection();
    if (!year || !make) return;
    fetchProductData(this.productsUrl)
      .then(({ products, owner }) => {
        const makeValue = tagValue(make);
        const models = tagsByPrefix(productsWithTags(products, [year, make]), "model:")
          .filter((modelTag) => modelBelongsToMake(modelTag, makeValue, owner));
        this.renderSelect(this.modelSel, models, this.placeholders.model, "model");
        if (!models.length) this.setStatus(STRINGS.noModels, true);
      })
      .catch(() => this.setStatus(STRINGS.unavailable, true));
  }

  onModelChange() {
    const { year, make, model } = this.selection();
    this.setSubmitDisabled(!(year && make && model));
    this.setStatus("");
    if (year && make && model) {
      window.location.href = buildTagUrl(this.allProductsUrl, [year, make, model]);
    }
  }

  onSearch() {
    const { year, make, model } = this.selection();
    if (!year || !make || !model) {
      this.setSubmitDisabled(true);
      this.setStatus(STRINGS.chooseFirst, true);
      return;
    }
    window.location.href = buildTagUrl(this.allProductsUrl, [year, make, model]);
  }
}

if (!customElements.get("eks-ymm-finder")) {
  customElements.define("eks-ymm-finder", EksYmmFinder);
}
